import { type Hex, isHex } from 'viem'

import type { ActionFlowAttribution } from '../types.action-flow'

/**
 * Sink for *runtime* calldata-attribution skips — a tagged call whose calldata
 * can't safely carry the suffix (see `applyAttribution` in `executeStep`). These
 * are data-dependent and can happen in production, so they're reported (never
 * thrown) and counted as an attribution coverage gap. An invalid `originTag` is
 * *not* one of these — it's a static misconfiguration that
 * {@link resolveAttributionOriginTag} throws on in dev/test. See
 * `ActionFlowProvider` for how reports are routed (logger → Sentry/BetterStack +
 * event tracker, with a `console.warn` fallback).
 */
export type AttributionIssueReporter = (reason: string, meta?: Record<string, unknown>) => void

const ORIGIN_TAG_BYTES = 4

// '0x' + exactly 8 hex chars. We check raw hex length, not `size()`: viem's
// `size()` rounds an odd-nibble value (e.g. `0x2222111`) *up* to 4 bytes, so a
// size-only check would accept it and emit odd-length (invalid) calldata that
// reverts the tx — so odd/short/long and non-hex tags must all be rejected.
const ORIGIN_TAG_HEX_LENGTH = 2 + ORIGIN_TAG_BYTES * 2

/**
 * Whether `originTag` is a well-formed origin tag: a 0x-prefixed, exactly
 * 4-byte (8 hex-char) value. Exported as the single source of truth for tag
 * validity so app-side env-var validation (e.g. a Zod refinement at the env
 * boundary) checks the same invariant the engine enforces.
 */
export function isValidOriginTag(originTag: Hex): boolean {
  return isHex(originTag, { strict: true }) && originTag.length === ORIGIN_TAG_HEX_LENGTH
}

/**
 * Validate a flow's attribution config once per execution and resolve it to a
 * usable origin tag. The single tag-validation choke point in the engine — kept
 * out of app builders so the 4-byte invariant holds repo-wide.
 *
 * An invalid tag is a static, app-authored misconfiguration, so we **fail fast**:
 * throw in dev/test (caught before shipping) and drop attribution in production
 * (a tracking concern must never block a user's tx). Apps reading the tag from
 * an env var should also validate at the env boundary via {@link isValidOriginTag}.
 *
 * @returns the 4-byte origin tag, or `undefined` when the flow opted out or —
 *   in production only — when the tag is invalid.
 * @throws in non-production when `attribution.originTag` is invalid.
 */
export function resolveAttributionOriginTag(
  attribution: ActionFlowAttribution | undefined
): Hex | undefined {
  if (!attribution) return undefined

  const { originTag } = attribution
  if (isValidOriginTag(originTag)) return originTag

  if (process.env.NODE_ENV !== 'production') {
    throw new Error(
      `Invalid attribution origin tag "${originTag}" — expected a 0x-prefixed 4-byte (8 hex-char) value`
    )
  }

  // Production: never block the flow over a misconfigured tag — drop attribution
  // and let the transaction go out untagged.
  return undefined
}
